import { type ReactElement, useState } from 'react';
import { type ReconcileResult } from '@vello/api-client';
import { runReconciliation } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Notice, PageHeader, Panel, FormRow, SectionTitle, Stack, Row, Text, Ink } from '@vello/web-theme';
import { CompensationRequestsPanel } from '../components/compensation-requests-panel';
import { MarketField } from '../components/market-field';

/**
 * Extrato (S114): ledger reconciliation over the real operator endpoint (`GET /v1/payments/reconcile`) — the
 * double-entry book nets to zero, and the PSP-captured total matches the ledger clearing account. Payout
 * requests and the payout approval queue live in **Repasses** (D318), matching the A02 Financeiro tree.
 */
export function FinanceScreen(): ReactElement {
  const { runtime } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const { fail } = useVocab();
  const [market, setMarket] = useState('');
  const [recon, setRecon] = useState<ReconcileResult | undefined>(undefined);
  const [reconError, setReconError] = useState<string | undefined>(undefined);
  const [reconBusy, setReconBusy] = useState(false);

  const onReconcile = async (): Promise<void> => {
    setReconBusy(true);
    setReconError(undefined);
    const result = await runReconciliation(runtime, market.trim());
    setReconBusy(false);
    if (result.ok) setRecon(result.value);
    else setReconError(t('fin.error.recon', { code: fail(result.error) }));
  };

  return (
    <>
      <PageHeader title={t('fin.title')} subtitle={t('fin.subtitle')} actions={<Button variant="ghost" onClick={() => navigate('finance_payouts')}>{t('fin.goPayouts')}</Button>} />

      <Panel><Stack gap={4}>
        <SectionTitle>{t('fin.reconciliation')}</SectionTitle>
        <FormRow
          action={
            <Button variant="primary" disabled={reconBusy || market.trim() === ''} onClick={() => void onReconcile()}>
              {reconBusy ? t('fin.run.busy') : t('fin.run')}
            </Button>
          }
        >
          <MarketField id="market" value={market} onChange={setMarket} hintId="fin.field.market.hint" />
        </FormRow>
        {reconError !== undefined && <Notice tone="danger">{reconError}</Notice>}
        {recon !== undefined && (
          <Stack gap={3}>
            <Row gap={3} align="center">
              <Text role="label" tone="secondary">{t('fin.ledger')}</Text>
              <Badge tone={recon.balanced ? 'success' : 'danger'}>{recon.balanced ? t('fin.balanced') : t('fin.unbalanced')}</Badge>
              {recon.sumMinor !== undefined && (
                <Ink tone="secondary">{t('fin.net', { n: recon.sumMinor })}</Ink>
              )}
            </Row>
            {recon.psp !== undefined && (
              <Row gap={3} align="center">
                <Text role="label" tone="secondary">{t('fin.pspVsLedger')}</Text>
                <Badge tone={recon.psp.matched ? 'success' : 'danger'}>{recon.psp.matched ? t('fin.matched') : t('fin.mismatch')}</Badge>
                <Ink tone="secondary">{t('fin.captured', { captured: recon.psp.pspCapturedMinor, clearing: recon.psp.ledgerClearingMinor })}</Ink>
              </Row>
            )}
          </Stack>
        )}
      </Stack></Panel>

      {/* D384: the compensations support asked for — the agent moves no money in a cash-only market. */}
      <CompensationRequestsPanel />
    </>
  );
}
